/**
 * Copyright 2016-2024, Optimizely
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { find, keyBy, assignBy } from '../utils/fns';
import { DATAFILE_VERSIONS, EXPERIMENT_TYPES, FEATURE_VARIABLE_TYPES } from '../utils/enums';
import { INVALID_DATAFILE_MALFORMED, INVALID_DATAFILE_VERSION, } from 'error_message';
import { OdpConfig } from '../odp/odp_config';
import { EXPERIMENT_KEY_NOT_IN_DATAFILE, FEATURE_NOT_IN_DATAFILE, INVALID_EXPERIMENT_ID, INVALID_EXPERIMENT_KEY, MISSING_INTEGRATION_KEY, UNABLE_TO_CAST_VALUE, UNEXPECTED_RESERVED_ATTRIBUTE_PREFIX, UNRECOGNIZED_ATTRIBUTE, VARIABLE_KEY_NOT_IN_DATAFILE, VARIATION_ID_NOT_IN_DATAFILE, LOCAL_HOLDOUT_MISSING_INCLUDED_RULES, } from 'error_message';
import { SKIPPING_JSON_VALIDATION, VALID_DATAFILE } from 'log_message';
import { OptimizelyError } from '../error/optimizly_error';
const EXPERIMENT_RUNNING_STATUS = 'Running';
const RESERVED_ATTRIBUTE_PREFIX = '$opt_';
const SUPPORTED_DATAFILE_VERSIONS = Object.values(DATAFILE_VERSIONS);
const parseAudienceConfig = (projectConfig) => {
    /*
     * Conditions of audiences in projectConfig.typedAudiences are not
     * expected to be string-encoded as they are here in projectConfig.audiences.
     */
    (projectConfig.audiences || []).forEach(audience => {
        audience.conditions = JSON.parse(audience.conditions);
    });
    projectConfig.audiencesById = {};
    assignBy(projectConfig.audiences, 'id', projectConfig.audiencesById);
    assignBy(projectConfig.typedAudiences, 'id', projectConfig.audiencesById);
};
const parseAttributeConfig = (projectConfig) => {
    projectConfig.attributes = projectConfig.attributes || [];
    projectConfig.attributeKeyMap = {};
    projectConfig.attributeIdMap = {};
    projectConfig.attributes.forEach(attribute => {
        projectConfig.attributeKeyMap[attribute.key] = attribute;
        projectConfig.attributeIdMap[attribute.id] = attribute;
    });
};
const parseGroupConfig = (projectConfig) => {
    projectConfig.groupIdMap = {};
    (projectConfig.groups || []).forEach((group) => {
        projectConfig.groupIdMap[group.id] = group;
        (group.experiments || []).forEach((experiment) => {
            experiment.groupId = group.id;
            projectConfig.experiments.push(experiment);
        });
    });
};
const parseRolloutConfig = (projectConfig) => {
    projectConfig.rolloutIdMap = {};
    (projectConfig.rollouts || []).forEach((rollout) => {
        projectConfig.rolloutIdMap[rollout.id] = rollout;
        (rollout.experiments || []).forEach((experiment) => {
            experiment.isRollout = true;
            projectConfig.experiments.push(experiment);
        });
    });
};
const parseIntegrationConfig = (projectConfig) => {
    const allSegmentsSet = new Set();
    Object.keys(projectConfig.audiencesById).forEach(id => {
        getAudienceSegments(projectConfig.audiencesById[id]).forEach(segment => {
            allSegmentsSet.add(segment);
        });
    });
    const allSegments = Array.from(allSegmentsSet);
    let odpIntegrated = false;
    let odpApiHost = '';
    let odpApiKey = '';
    let odpPixelUrl = '';
    projectConfig.integrationKeyMap = {};
    (projectConfig.integrations || []).forEach(integration => {
        if (!('key' in integration)) {
            throw new OptimizelyError(MISSING_INTEGRATION_KEY);
        }
        projectConfig.integrationKeyMap[integration.key] = integration;
        if (integration.key === 'odp') {
            odpIntegrated = true;
            odpApiKey = odpApiKey || integration.publicKey || '';
            odpApiHost = odpApiHost || integration.host || '';
            odpPixelUrl = odpPixelUrl || integration.pixelUrl || '';
        }
    });
    if (odpIntegrated) {
        projectConfig.odpIntegrationConfig = {
            integrated: true,
            odpConfig: new OdpConfig(odpApiKey, odpApiHost, odpPixelUrl, allSegments),
        };
    }
    else {
        projectConfig.odpIntegrationConfig = { integrated: false };
    }
};
const parseExperimentConfig = (projectConfig) => {
    projectConfig.experimentKeyMap = {};
    projectConfig.experimentIdMap = {};
    projectConfig.variationIdMap = {};
    projectConfig.variationVariableUsageMap = {};
    (projectConfig.experiments || []).forEach((experiment) => {
        projectConfig.experimentKeyMap[experiment.key] = experiment;
        projectConfig.experimentIdMap[experiment.id] = experiment;
        experiment.variationKeyMap = {};
        (experiment.variations || []).forEach((variation) => {
            experiment.variationKeyMap[variation.key] = variation;
            projectConfig.variationIdMap[variation.id] = variation;
            if (variation.variables) {
                projectConfig.variationVariableUsageMap[variation.id] = keyBy(variation.variables, 'id');
            }
        });
    });
};
const parseFeatureFlagConfig = (projectConfig) => {
    // Object containing experiment Ids that exist in any feature
    // for checking that experiment is a feature experiment or not.
    projectConfig.experimentFeatureMap = {};
    projectConfig.featureKeyMap = {};
    // all rules (experiment rules and delivery rules) for each flag
    projectConfig.flagRulesMap = {};
    projectConfig.flagVariationsMap = {};
    (projectConfig.featureFlags || []).forEach((feature) => {
        projectConfig.featureKeyMap[feature.key] = feature;
        feature.variableKeyMap = {};
        feature.variables.forEach(variable => {
            // Json type is represented in datafile as a subtype of string for the sake of backwards compatibility.
            // Converting it to a first-class json type while creating Project Config
            if (variable.type === FEATURE_VARIABLE_TYPES.STRING && variable.subType === FEATURE_VARIABLE_TYPES.JSON) {
                variable.type = FEATURE_VARIABLE_TYPES.JSON;
                delete variable.subType;
            }
            feature.variableKeyMap[variable.key] = variable;
        });
        const flagRuleExperiments = [];
        // all variations for the flag mapped by id
        const flagVariationsById = {};
        const everyoneElseVariation = getEveryoneElseVariation(projectConfig, feature);
        (feature.experimentIds || []).forEach(experimentId => {
            projectConfig.experimentFeatureMap[experimentId] =
                projectConfig.experimentFeatureMap[experimentId] || [];
            projectConfig.experimentFeatureMap[experimentId].push(feature.id);
            const experiment = projectConfig.experimentIdMap[experimentId];
            flagRuleExperiments.push(experiment);
            // Inject "everyone else" variation into feature rollout (FR) experiments
            if (experiment && experiment.type === EXPERIMENT_TYPES.FR && everyoneElseVariation) {
                experiment.variations.push(everyoneElseVariation);
                experiment.trafficAllocation.push({
                    entityId: everyoneElseVariation.id,
                    endOfRange: 10000,
                });
                // Update variation lookup map
                experiment.variationKeyMap[everyoneElseVariation.key] = everyoneElseVariation;
            }
            (experiment.variations || []).forEach((variation) => {
                flagVariationsById[variation.id] = variation;
            });
        });
        // add all rollout experiments to the flagRuleExperiments list
        const rollout = projectConfig.rolloutIdMap[feature.rolloutId];
        if (rollout) {
            (rollout.experiments || []).forEach((experiment) => {
                flagRuleExperiments.push(experiment);
                (experiment.variations || []).forEach((variation) => {
                    flagVariationsById[variation.id] = variation;
                });
            });
        }
        projectConfig.flagRulesMap[feature.key] = flagRuleExperiments;
        projectConfig.flagVariationsMap[feature.key] = Object.values(flagVariationsById);
    });
};
/**
 * Creates projectConfig object to be used for quick project property lookup
 * @param  {string}        datafileStr   JSON string representation of the datafile
 * @return {ProjectConfig} Object representing project configuration
 */
export const createProjectConfig = function (datafileStr, options) {
    var _a, _b;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let projectConfig;
    try {
        projectConfig = JSON.parse(datafileStr);
    }
    catch (_c) {
        throw new OptimizelyError(INVALID_DATAFILE_MALFORMED);
    }
    const version = projectConfig.version;
    if (SUPPORTED_DATAFILE_VERSIONS.indexOf(version) === -1) {
        throw new OptimizelyError(INVALID_DATAFILE_VERSION, version);
    }
    if (options === null || options === void 0 ? void 0 : options.jsonSchemaValidator) {
        options.jsonSchemaValidator(projectConfig);
        (_a = options.logger) === null || _a === void 0 ? void 0 : _a.info(VALID_DATAFILE);
    }
    else {
        (_b = options === null || options === void 0 ? void 0 : options.logger) === null || _b === void 0 ? void 0 : _b.info(SKIPPING_JSON_VALIDATION);
    }
    if (!projectConfig.region) {
        projectConfig.region = 'US';
    }
    projectConfig.__datafileStr = datafileStr;
    parseAudienceConfig(projectConfig);
    parseAttributeConfig(projectConfig);
    projectConfig.eventKeyMap = keyBy(projectConfig.events, 'key');
    parseGroupConfig(projectConfig);
    parseRolloutConfig(projectConfig);
    parseIntegrationConfig(projectConfig);
    parseExperimentConfig(projectConfig);
    parseFeatureFlagConfig(projectConfig);
    parseHoldoutsConfig(projectConfig, options === null || options === void 0 ? void 0 : options.logger);
    return projectConfig;
};
/**
 * Get the "everyone else" variation from the last rule in the flag's rollout.
 * Returns null if the rollout cannot be resolved or has no variations.
 */
const getEveryoneElseVariation = function (projectConfig, featureFlag) {
    if (!featureFlag.rolloutId) {
        return null;
    }
    const rollout = projectConfig.rolloutIdMap[featureFlag.rolloutId];
    if (!rollout || !rollout.experiments || rollout.experiments.length === 0) {
        return null;
    }
    const everyoneElseRule = rollout.experiments[rollout.experiments.length - 1];
    if (!everyoneElseRule.variations || everyoneElseRule.variations.length === 0) {
        return null;
    }
    return everyoneElseRule.variations[0];
};
/**
 * Parse holdouts from the two top-level datafile sections.
 *
 * Two top-level sections drive holdout scoping (Gen 3+):
 *   - `holdouts`      → ALL entries are global holdouts (applied to every flag).
 *                       Any `includedRules` field on these entries is IGNORED;
 *                       section membership alone determines scope.
 *   - `localHoldouts` → ALL entries are local holdouts (rule-scoped via
 *                       `includedRules`). Entries missing/with empty `includedRules`
 *                       are invalid and skipped with an error log.
 *
 * Backward compatibility: older datafiles that only emit the `holdouts` section
 * continue to work — every entry is treated as global, matching pre-localHoldouts
 * behavior. The `localHoldouts` key is simply absent and parsed as an empty list.
 */
const parseHoldoutsConfig = (projectConfig, logger) => {
    projectConfig.holdouts = projectConfig.holdouts || [];
    projectConfig.localHoldouts = projectConfig.localHoldouts || [];
    // const global: Holdout[] = [];
    const ruleHoldoutsMap = {};
    const holdoutIdMap = {};
    // Helper to seed common per-holdout fields (matches legacy behavior).
    const initHoldout = (holdout) => {
        // Original design of holdouts made use of the includeFlags and excludeFlags fields to identify local holdouts.
        // But this was never released. In the current design, these fields are no longer used. These fields are kept
        // and assigned empty array to keep the published type `Holdout` unchanged.
        holdout.includedFlags = [];
        holdout.excludedFlags = [];
        holdout.variationKeyMap = keyBy(holdout.variations, 'key');
        assignBy(holdout.variations, 'id', projectConfig.variationIdMap);
    };
    // Process global holdouts: section membership is the sole signal for scope.
    // Drop any `includedRules` field on entries here so the entity is unambiguously
    // global (isGlobal === true), even if the datafile incorrectly includes one.
    projectConfig.holdouts.forEach((holdout) => {
        initHoldout(holdout);
        delete holdout.includedRules;
        holdout.isGlobal = true;
        holdoutIdMap[holdout.id] = holdout;
    });
    // Process local holdouts: every entry must carry a non-empty `includedRules` list.
    // Entries missing it (or with [] / null) are invalid per spec — log an error and
    // exclude from evaluation. Do NOT fall back to global application — the partition
    // between sections is hard.
    projectConfig.localHoldouts.forEach((holdout) => {
        const includedRules = holdout.includedRules;
        if (!Array.isArray(includedRules) || includedRules.length === 0) {
            logger === null || logger === void 0 ? void 0 : logger.error(LOCAL_HOLDOUT_MISSING_INCLUDED_RULES, holdout.key);
            return;
        }
        initHoldout(holdout);
        holdout.isGlobal = false;
        holdoutIdMap[holdout.id] = holdout;
        for (const ruleId of includedRules) {
            if (!ruleHoldoutsMap[ruleId]) {
                ruleHoldoutsMap[ruleId] = [];
            }
            ruleHoldoutsMap[ruleId].push(holdout);
        }
    });
    projectConfig.holdoutIdMap = holdoutIdMap;
    projectConfig.ruleHoldoutsMap = ruleHoldoutsMap;
};
/**
 * Returns all global holdouts (parsed from the top-level `holdouts` section).
 * Section membership in `holdouts` is the sole signal for global scope —
 * any `includedRules` field on these entries is ignored.
 */
export const getGlobalHoldouts = (projectConfig) => {
    return projectConfig.holdouts;
};
/**
 * Returns local holdouts targeting the given rule ID, or an empty array.
 *
 * Local holdouts come from the top-level `localHoldouts` datafile section and
 * are scoped per-rule via their `includedRules` field.
 */
export const getHoldoutsForRule = (projectConfig, ruleId) => {
    var _a;
    return (_a = projectConfig.ruleHoldoutsMap[ruleId]) !== null && _a !== void 0 ? _a : [];
};
/**
 * Extract all audience segments used in this audience's conditions
 * @param  {Audience}     audience  Object representing the audience being parsed
 * @return {string[]}               List of all audience segments
 */
export const getAudienceSegments = function (audience) {
    if (!audience.conditions)
        return [];
    return getSegmentsFromConditions(audience.conditions);
};
// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const getSegmentsFromConditions = (condition) => {
    const segments = [];
    if (isLogicalOperator(condition)) {
        return [];
    }
    else if (Array.isArray(condition)) {
        condition.forEach(nextCondition => segments.push(...getSegmentsFromConditions(nextCondition)));
    }
    else if (condition['match'] === 'qualified') {
        segments.push(condition['value']);
    }
    return segments;
};
function isLogicalOperator(condition) {
    return ['and', 'or', 'not'].includes(condition);
}
/**
 * Get experiment ID for the provided experiment key
 * @param  {ProjectConfig}    projectConfig   Object representing project configuration
 * @param  {string}           experimentKey   Experiment key for which ID is to be determined
 * @return {string}                           Experiment ID corresponding to the provided experiment key
 * @throws If experiment key is not in datafile
 */
export const getExperimentId = function (projectConfig, experimentKey) {
    const experiment = projectConfig.experimentKeyMap[experimentKey];
    if (!experiment) {
        throw new OptimizelyError(INVALID_EXPERIMENT_KEY, experimentKey);
    }
    return experiment.id;
};
/**
 * Get layer ID for the provided experiment key
 * @param  {ProjectConfig}    projectConfig   Object representing project configuration
 * @param  {string}           experimentId    Experiment ID for which layer ID is to be determined
 * @return {string}                           Layer ID corresponding to the provided experiment key
 * @throws If experiment key is not in datafile
 */
export const getLayerId = function (projectConfig, experimentId) {
    const experiment = projectConfig.experimentIdMap[experimentId];
    if (!experiment) {
        throw new OptimizelyError(INVALID_EXPERIMENT_ID, experimentId);
    }
    return experiment.layerId;
};
/**
 * Get attribute ID for the provided attribute key
 * @param  {ProjectConfig}   projectConfig    Object representing project configuration
 * @param  {string}          attributeKey     Attribute key for which ID is to be determined
 * @param  {LogHandler}      logger
 * @return {string|null}     Attribute ID corresponding to the provided attribute key. Attribute key if it is a reserved attribute.
 */
export const getAttributeId = function (projectConfig, attributeKey, logger) {
    const attribute = projectConfig.attributeKeyMap[attributeKey];
    const hasReservedPrefix = attributeKey.indexOf(RESERVED_ATTRIBUTE_PREFIX) === 0;
    if (attribute) {
        if (hasReservedPrefix) {
            logger === null || logger === void 0 ? void 0 : logger.warn(UNEXPECTED_RESERVED_ATTRIBUTE_PREFIX, attributeKey, RESERVED_ATTRIBUTE_PREFIX);
        }
        return attribute.id;
    }
    else if (hasReservedPrefix) {
        return attributeKey;
    }
    logger === null || logger === void 0 ? void 0 : logger.warn(UNRECOGNIZED_ATTRIBUTE, attributeKey);
    return null;
};
/**
 * Get event ID for the provided
 * @param  {ProjectConfig}   projectConfig  Object representing project configuration
 * @param  {string}          eventKey       Event key for which ID is to be determined
 * @return {string|null}     Event ID corresponding to the provided event key
 */
export const getEventId = function (projectConfig, eventKey) {
    const event = projectConfig.eventKeyMap[eventKey];
    if (event) {
        return event.id;
    }
    return null;
};
/**
 * Get experiment status for the provided experiment key
 * @param  {ProjectConfig}  projectConfig   Object representing project configuration
 * @param  {string}         experimentKey   Experiment key for which status is to be determined
 * @return {string}         Experiment status corresponding to the provided experiment key
 * @throws If experiment key is not in datafile
 */
export const getExperimentStatus = function (projectConfig, experimentKey) {
    const experiment = projectConfig.experimentKeyMap[experimentKey];
    if (!experiment) {
        throw new OptimizelyError(INVALID_EXPERIMENT_KEY, experimentKey);
    }
    return experiment.status;
};
/**
 * Returns whether experiment has a status of 'Running'
 * @param  {ProjectConfig}  projectConfig   Object representing project configuration
 * @param  {string}         experimentKey   Experiment key for which status is to be compared with 'Running'
 * @return {boolean}                        True if experiment status is set to 'Running', false otherwise
 */
export const isActive = function (projectConfig, experimentKey) {
    return getExperimentStatus(projectConfig, experimentKey) === EXPERIMENT_RUNNING_STATUS;
};
/**
 * Determine for given experiment if event is running, which determines whether should be dispatched or not
 * @param  {ProjectConfig}  configObj       Object representing project configuration
 * @param  {string}         experimentKey   Experiment key for which the status is to be determined
 * @return {boolean}                        True if the experiment is running
 *                                          False if the experiment is not running
 *
 */
export const isRunning = function (projectConfig, experimentKey) {
    return getExperimentStatus(projectConfig, experimentKey) === EXPERIMENT_RUNNING_STATUS;
};
/**
 * Get audience conditions for the experiment
 * @param  {ProjectConfig}  projectConfig   Object representing project configuration
 * @param  {string}         experimentId    Experiment id for which audience conditions are to be determined
 * @return {Array<string|string[]>}         Audience conditions for the experiment - can be an array of audience IDs, or a
 *                                          nested array of conditions
 *                                          Examples: ["5", "6"], ["and", ["or", "1", "2"], "3"]
 * @throws If experiment key is not in datafile
 */
export const getExperimentAudienceConditions = function (projectConfig, experimentId) {
    const experiment = projectConfig.experimentIdMap[experimentId];
    if (!experiment) {
        throw new OptimizelyError(INVALID_EXPERIMENT_ID, experimentId);
    }
    return experiment.audienceConditions || experiment.audienceIds;
};
/**
 * Get variation key given experiment key and variation ID
 * @param  {ProjectConfig}  projectConfig   Object representing project configuration
 * @param  {string}         variationId     ID of the variation
 * @return {string|null}    Variation key or null if the variation ID is not found
 */
export const getVariationKeyFromId = function (projectConfig, variationId) {
    if (projectConfig.variationIdMap.hasOwnProperty(variationId)) {
        return projectConfig.variationIdMap[variationId].key;
    }
    return null;
};
/**
 * Get variation given variation ID
 * @param  {ProjectConfig}  projectConfig   Object representing project configuration
 * @param  {string}         variationId     ID of the variation
 * @return {Variation|null}    Variation or null if the variation ID is not found
 */
export const getVariationFromId = function (projectConfig, variationId) {
    if (projectConfig.variationIdMap.hasOwnProperty(variationId)) {
        return projectConfig.variationIdMap[variationId];
    }
    return null;
};
/**
 * Get the variation ID given the experiment key and variation key
 * @param  {ProjectConfig}  projectConfig   Object representing project configuration
 * @param  {string}         experimentKey   Key of the experiment the variation belongs to
 * @param  {string}         variationKey    The variation key
 * @return {string|null}    Variation ID or null
 */
export const getVariationIdFromExperimentAndVariationKey = function (projectConfig, experimentKey, variationKey) {
    const experiment = projectConfig.experimentKeyMap[experimentKey];
    if (experiment.variationKeyMap.hasOwnProperty(variationKey)) {
        return experiment.variationKeyMap[variationKey].id;
    }
    return null;
};
/**
 * Get experiment from provided experiment key
 * @param  {ProjectConfig}  projectConfig  Object representing project configuration
 * @param  {string}         experimentKey  Event key for which experiment IDs are to be retrieved
 * @return {Experiment}     Experiment
 * @throws If experiment key is not in datafile
 */
export const getExperimentFromKey = function (projectConfig, experimentKey) {
    if (projectConfig.experimentKeyMap.hasOwnProperty(experimentKey)) {
        const experiment = projectConfig.experimentKeyMap[experimentKey];
        if (experiment) {
            return experiment;
        }
    }
    throw new OptimizelyError(EXPERIMENT_KEY_NOT_IN_DATAFILE, experimentKey);
};
/**
 * Given an experiment id, returns the traffic allocation within that experiment
 * @param  {ProjectConfig}  projectConfig  Object representing project configuration
 * @param  {string}         experimentId   Id representing the experiment
 * @return {TrafficAllocation[]}           Traffic allocation for the experiment
 * @throws If experiment key is not in datafile
 */
export const getTrafficAllocation = function (projectConfig, experimentId) {
    const experiment = projectConfig.experimentIdMap[experimentId];
    if (!experiment) {
        throw new OptimizelyError(INVALID_EXPERIMENT_ID, experimentId);
    }
    return experiment.trafficAllocation;
};
/**
 * Get experiment from provided experiment id. Log an error if no experiment
 * exists in the project config with the given ID.
 * @param  {ProjectConfig}  projectConfig  Object representing project configuration
 * @param  {string}         experimentId   ID of desired experiment object
 * @param  {LogHandler}     logger
 * @return {Experiment|null}               Experiment object or null
 */
export const getExperimentFromId = function (projectConfig, experimentId, logger) {
    if (projectConfig.experimentIdMap.hasOwnProperty(experimentId)) {
        const experiment = projectConfig.experimentIdMap[experimentId];
        if (experiment) {
            return experiment;
        }
    }
    logger === null || logger === void 0 ? void 0 : logger.error(INVALID_EXPERIMENT_ID, experimentId);
    return null;
};
/**
 * Returns flag variation for specified flagKey and variationKey
 * @param  {flagKey}        string
 * @param  {variationKey}   string
 * @return {Variation|null}
 */
export const getFlagVariationByKey = function (projectConfig, flagKey, variationKey) {
    if (!projectConfig) {
        return null;
    }
    const variations = projectConfig.flagVariationsMap[flagKey];
    const result = find(variations, item => item.key === variationKey);
    if (result) {
        return result;
    }
    return null;
};
/**
 * Get feature from provided feature key. Log an error if no feature exists in
 * the project config with the given key.
 * @param  {ProjectConfig}    projectConfig
 * @param  {string}           featureKey
 * @param  {LogHandler}       logger
 * @return {FeatureFlag|null} Feature object, or null if no feature with the given
 *                            key exists
 */
export const getFeatureFromKey = function (projectConfig, featureKey, logger) {
    if (projectConfig.featureKeyMap.hasOwnProperty(featureKey)) {
        const feature = projectConfig.featureKeyMap[featureKey];
        if (feature) {
            return feature;
        }
    }
    logger === null || logger === void 0 ? void 0 : logger.error(FEATURE_NOT_IN_DATAFILE, featureKey);
    return null;
};
/**
 * Get the variable with the given key associated with the feature with the
 * given key. If the feature key or the variable key are invalid, log an error
 * message.
 * @param  {ProjectConfig}        projectConfig
 * @param  {string}               featureKey
 * @param  {string}               variableKey
 * @param  {LogHandler}           logger
 * @return {FeatureVariable|null} Variable object, or null one or both of the given
 * feature and variable keys are invalid
 */
export const getVariableForFeature = function (projectConfig, featureKey, variableKey, logger) {
    const feature = projectConfig.featureKeyMap[featureKey];
    if (!feature) {
        logger === null || logger === void 0 ? void 0 : logger.error(FEATURE_NOT_IN_DATAFILE, featureKey);
        return null;
    }
    const variable = feature.variableKeyMap[variableKey];
    if (!variable) {
        logger === null || logger === void 0 ? void 0 : logger.error(VARIABLE_KEY_NOT_IN_DATAFILE, variableKey, featureKey);
        return null;
    }
    return variable;
};
/**
 * Get the value of the given variable for the given variation. If the given
 * variable has no value for the given variation, return null. Log an error message if the variation is invalid. If the
 * variable or variation are invalid, return null.
 * @param  {ProjectConfig}     projectConfig
 * @param  {FeatureVariable}   variable
 * @param  {Variation}         variation
 * @param  {LogHandler}        logger
 * @return {string|null}       The value of the given variable for the given
 * variation, or null if the given variable has no value
 * for the given variation or if the variation or variable are invalid
 */
export const getVariableValueForVariation = function (projectConfig, variable, variation, logger) {
    if (!variable || !variation) {
        return null;
    }
    if (!projectConfig.variationVariableUsageMap.hasOwnProperty(variation.id)) {
        logger === null || logger === void 0 ? void 0 : logger.error(VARIATION_ID_NOT_IN_DATAFILE, variation.id);
        return null;
    }
    const variableUsages = projectConfig.variationVariableUsageMap[variation.id];
    const variableUsage = variableUsages[variable.id];
    return variableUsage ? variableUsage.value : null;
};
/**
 * Given a variable value in string form, try to cast it to the argument type.
 * If the type cast succeeds, return the type casted value, otherwise log an
 * error and return null.
 * @param {string}     variableValue  Variable value in string form
 * @param {string}     variableType   Type of the variable whose value was passed
 *                                    in the first argument. Must be one of
 *                                    FEATURE_VARIABLE_TYPES in
 *                                    lib/utils/enums/index.js. The return value's
 *                                    type is determined by this argument (boolean
 *                                    for BOOLEAN, number for INTEGER or DOUBLE,
 *                                    and string for STRING).
 * @param {LogHandler} logger         Logger instance
 * @returns {*}                       Variable value of the appropriate type, or
 *                                    null if the type cast failed
 */
export const getTypeCastValue = function (variableValue, variableType, logger) {
    let castValue;
    switch (variableType) {
        case FEATURE_VARIABLE_TYPES.BOOLEAN:
            if (variableValue !== 'true' && variableValue !== 'false') {
                logger === null || logger === void 0 ? void 0 : logger.error(UNABLE_TO_CAST_VALUE, variableValue, variableType);
                castValue = null;
            }
            else {
                castValue = variableValue === 'true';
            }
            break;
        case FEATURE_VARIABLE_TYPES.INTEGER:
            castValue = parseInt(variableValue, 10);
            if (isNaN(castValue)) {
                logger === null || logger === void 0 ? void 0 : logger.error(UNABLE_TO_CAST_VALUE, variableValue, variableType);
                castValue = null;
            }
            break;
        case FEATURE_VARIABLE_TYPES.DOUBLE:
            castValue = parseFloat(variableValue);
            if (isNaN(castValue)) {
                logger === null || logger === void 0 ? void 0 : logger.error(UNABLE_TO_CAST_VALUE, variableValue, variableType);
                castValue = null;
            }
            break;
        case FEATURE_VARIABLE_TYPES.JSON:
            try {
                castValue = JSON.parse(variableValue);
            }
            catch (e) {
                logger === null || logger === void 0 ? void 0 : logger.error(UNABLE_TO_CAST_VALUE, variableValue, variableType);
                castValue = null;
            }
            break;
        default:
            // type is STRING
            castValue = variableValue;
            break;
    }
    return castValue;
};
/**
 * Returns an object containing all audiences in the project config. Keys are audience IDs
 * and values are audience objects.
 * @param   {ProjectConfig}     projectConfig
 * @returns {{ [id: string]: Audience }}
 */
export const getAudiencesById = function (projectConfig) {
    return projectConfig.audiencesById;
};
/**
 * Returns true if an event with the given key exists in the datafile, and false otherwise
 * @param   {ProjectConfig}     projectConfig
 * @param   {string}            eventKey
 * @returns {boolean}
 */
export const eventWithKeyExists = function (projectConfig, eventKey) {
    return projectConfig.eventKeyMap.hasOwnProperty(eventKey);
};
/**
 * Returns true if experiment belongs to any feature, false otherwise.
 * @param   {ProjectConfig}       projectConfig
 * @param   {string}              experimentId
 * @returns {boolean}
 */
export const isFeatureExperiment = function (projectConfig, experimentId) {
    return projectConfig.experimentFeatureMap.hasOwnProperty(experimentId);
};
/**
 * Returns the JSON string representation of the datafile
 * @param   {ProjectConfig}       projectConfig
 * @returns {string}
 */
export const toDatafile = function (projectConfig) {
    return projectConfig.__datafileStr;
};
/**
 * Get the send flag decisions value
 * @param  {ProjectConfig}   projectConfig
 * @return {boolean}         A boolean value that indicates if we should send flag decisions
 */
export const getSendFlagDecisionsValue = function (projectConfig) {
    return !!projectConfig.sendFlagDecisions;
};
export default {
    createProjectConfig,
    getExperimentId,
    getLayerId,
    getAttributeId,
    getEventId,
    getExperimentStatus,
    isActive,
    isRunning,
    getExperimentAudienceConditions,
    getVariationFromId,
    getVariationKeyFromId,
    getVariationIdFromExperimentAndVariationKey,
    getExperimentFromKey,
    getExperimentFromId,
    getFlagVariationByKey,
    getFeatureFromKey,
    getVariableForFeature,
    getVariableValueForVariation,
    getTypeCastValue,
    getSendFlagDecisionsValue,
    getAudiencesById,
    getAudienceSegments,
    eventWithKeyExists,
    isFeatureExperiment,
    toDatafile,
    getTrafficAllocation,
};
export const __platforms = ['__universal__'];
//# sourceMappingURL=project_config.js.map